/**
 * Public answer rating. No session is involved — the signed link token is the credential —
 * so a 401 must never bounce the reader to the login page.
 */
import type { FeedbackContextResponse, FeedbackSubmitBody, FeedbackSubmitResponse } from '@portal/contracts'
import { apiFetch, PortalApiError } from './portal-client'

export function getFeedbackContext(token: string): Promise<FeedbackContextResponse> {
  return apiFetch<FeedbackContextResponse>(
    '/api/public/feedback/context',
    { method: 'POST', body: JSON.stringify({ token }) },
    { on401: 'throw' }
  )
}

export function submitFeedback(body: FeedbackSubmitBody): Promise<FeedbackSubmitResponse> {
  return apiFetch<FeedbackSubmitResponse>(
    '/api/public/feedback/submit',
    { method: 'POST', body: JSON.stringify(body) },
    { on401: 'throw' }
  )
}

/**
 * Whether the context read says the link cannot be used: every bad link answers the same
 * `feedback_link_invalid` (404), and a 400 means the BFF refused what was sent as a link.
 */
export function isInvalidFeedbackLink(err: unknown): boolean {
  return (
    err instanceof PortalApiError &&
    (err.issue?.code === 'feedback_link_invalid' || err.status === 404 || err.status === 400)
  )
}

/** A refusal (4xx) says the same thing on every try — a busy relay included, which asking again only keeps busy. */
export function isRefusal(err: unknown): boolean {
  return err instanceof PortalApiError && err.status >= 400 && err.status < 500
}
